package com.evoapps.evoshift.ui.courses

import com.evoapps.evoshift.widgets.SurfaceCard
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import org.jetbrains.compose.resources.StringResource
import com.evoapps.evoshift.ui.CardHairline
import com.evoapps.evoshift.widgets.OnScreen
import com.evoapps.evoshift.widgets.WarningNote
import androidx.compose.material.icons.filled.HealthAndSafety
import androidx.compose.material.icons.filled.School
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.HorizontalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.Course
import com.evoapps.evoshift.domain.model.MyCourse
import com.evoapps.evoshift.domain.model.OhsConfirmation
import com.evoapps.evoshift.domain.model.OhsRecord
import com.evoapps.evoshift.domain.model.TrainingState
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.ContentMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.user.formatDay
import com.evoapps.evoshift.ui.util.HelpPage
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.widgets.helpAction
import com.evoapps.evoshift.viewmodels.CoursesViewModel
import com.evoapps.evoshift.viewmodels.MyTrainingViewModel
import com.evoapps.evoshift.viewmodels.TeamTrainingRow
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.course_due_on
import evoshift.composeapp.generated.resources.course_edit
import evoshift.composeapp.generated.resources.course_new
import evoshift.composeapp.generated.resources.course_open
import evoshift.composeapp.generated.resources.course_overdue_since
import evoshift.composeapp.generated.resources.course_pages_seen
import evoshift.composeapp.generated.resources.course_passed_with
import evoshift.composeapp.generated.resources.course_preview
import evoshift.composeapp.generated.resources.course_progress
import evoshift.composeapp.generated.resources.course_status_draft
import evoshift.composeapp.generated.resources.course_status_published
import evoshift.composeapp.generated.resources.ohs_open_record
import evoshift.composeapp.generated.resources.ohs_my_record
import evoshift.composeapp.generated.resources.ohs_to_confirm_general
import evoshift.composeapp.generated.resources.ohs_to_confirm_station
import evoshift.composeapp.generated.resources.ohs_record_complete
import evoshift.composeapp.generated.resources.ohs_record_incomplete
import evoshift.composeapp.generated.resources.training_card_confirmations
import evoshift.composeapp.generated.resources.training_card_overdue
import evoshift.composeapp.generated.resources.training_card_to_do
import evoshift.composeapp.generated.resources.training_courses
import evoshift.composeapp.generated.resources.training_courses_empty
import evoshift.composeapp.generated.resources.training_intro
import evoshift.composeapp.generated.resources.training_mine
import evoshift.composeapp.generated.resources.training_mine_empty
import evoshift.composeapp.generated.resources.training_status_counts
import evoshift.composeapp.generated.resources.training_status_none
import evoshift.composeapp.generated.resources.training_team
import evoshift.composeapp.generated.resources.training_title
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource

const val TRAINING_CARD = "training_card"
fun courseRowTag(courseId: String) = "course_row_$courseId"
fun managedCourseTag(courseId: String) = "managed_course_$courseId"
fun teamTrainingTag(userId: String) = "team_training_$userId"

internal val TrainingDoneColor = Color(0xFF2E7D32)
internal val TrainingOverdueColor = Color(0xFFB3261E)

/**
 * SH-126: More → Training. Everyone: their courses (to do first) and their OHS training record.
 * Managers: the workplace's courses (new, edit, progress, preview) and the team's training status
 * with each person's OHS record.
 */
@Composable
fun CoursesScreen(
    viewModel: CoursesViewModel,
    onNavigateBack: () -> Unit,
    onOpenCourse: (courseId: String) -> Unit,
    onEditCourse: (courseId: String?) -> Unit,
    onOpenProgress: (courseId: String) -> Unit,
    onOpenOhs: (userId: String?) -> Unit
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val shown = state.error?.asString()
    LaunchedEffect(shown) {
        shown?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.messageShown()
        }
    }
    // Back from a course or the editor: the list follows.
    LaunchedEffect(Unit) { viewModel.refresh() }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = { EvoTopBar(title = stringResource(Res.string.training_title), onBack = onNavigateBack, actions = listOf(helpAction(HelpPage.TRAINING))) }
    ) { padding ->
        Box(Modifier.padding(padding).fillMaxSize().contentWidth(ContentMaxWidth)) {
            if (state.isLoading) {
                CircularProgressIndicator(Modifier.align(Alignment.Center))
                return@Box
            }
            Column(
                modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState()).padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Text(stringResource(Res.string.training_intro), style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                if (state.canTake) {
                    SectionTitle(stringResource(Res.string.training_mine))
                    state.myOhs?.let { record -> MyOhsCard(record, isOwner = state.myOhsIsOwner, onOpen = { onOpenOhs(null) }) }
                    if (state.myCourses.isEmpty()) {
                        Text(stringResource(Res.string.training_mine_empty), color = MaterialTheme.colorScheme.onSurfaceVariant)
                    }
                    state.myCourses.forEach { mine -> MyCourseCard(mine, state.today, onOpen = { onOpenCourse(mine.course.id) }) }
                }
                if (state.canSeeProgress) {
                    SectionTitle(stringResource(Res.string.training_courses))
                    if (state.canManage) {
                        Button(onClick = { onEditCourse(null) }) {
                            Icon(Icons.Default.Add, contentDescription = null)
                            Spacer(Modifier.width(8.dp))
                            Text(stringResource(Res.string.course_new))
                        }
                    }
                    if (state.managed.isEmpty()) {
                        Text(stringResource(Res.string.training_courses_empty), color = MaterialTheme.colorScheme.onSurfaceVariant)
                    }
                    state.managed.forEach { course ->
                        ManagedCourseCard(
                            course = course,
                            onEdit = if (state.canManage) { { onEditCourse(course.id) } } else null,
                            onProgress = { onOpenProgress(course.id) },
                            onPreview = { onOpenCourse(course.id) }
                        )
                    }
                    if (state.team.isNotEmpty()) {
                        SectionTitle(stringResource(Res.string.training_team))
                        // SH-230: the team in one card, a row each, like the other lists.
                        SurfaceCard(Modifier.fillMaxWidth()) {
                            state.team.forEachIndexed { index, row ->
                                if (index > 0) HorizontalDivider(color = CardHairline)
                                TeamRow(row, onOpenOhs = { onOpenOhs(row.userId) })
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SectionTitle(text: String) {
    Text(text, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, modifier = Modifier.padding(top = 8.dp))
}

@Composable
private fun MyOhsCard(record: OhsRecord, isOwner: Boolean, onOpen: () -> Unit) {
    // SH-230: what is still to do reads as a warning (an amber edge and strips), not as a grey line.
    // SH-244: the owner has no initial training: their card only opens the periodic training.
    val toDo = !isOwner && (!record.initialDone || record.pendingConfirmations.isNotEmpty())
    SurfaceCard(Modifier.fillMaxWidth(), warning = toDo) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Default.HealthAndSafety, contentDescription = null, tint = MaterialTheme.colorScheme.primary)
                Spacer(Modifier.width(8.dp))
                Text(stringResource(Res.string.ohs_my_record), fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
            }
            if (!isOwner) record.pendingConfirmations.forEach { part ->
                WarningNote(stringResource(if (part == OhsConfirmation.GENERAL) Res.string.ohs_to_confirm_general else Res.string.ohs_to_confirm_station))
            }
            if (isOwner) {
                // Nothing to confirm or complete: the periodic training is in the record.
            } else if (record.initialDone) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Default.CheckCircle, contentDescription = null, tint = OnScreen.success, modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text(stringResource(Res.string.ohs_record_complete), style = MaterialTheme.typography.bodyMedium, color = OnScreen.success)
                }
            } else {
                WarningNote(stringResource(Res.string.ohs_record_incomplete))
            }
            CardActions { CardAction(Res.string.ohs_open_record, onOpen) }
        }
    }
}

@Composable
private fun MyCourseCard(mine: MyCourse, today: LocalDate, onOpen: () -> Unit) {
    SurfaceCard(
        modifier = Modifier.fillMaxWidth().testTag(courseRowTag(mine.course.id))
    ) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Default.School, contentDescription = null, tint = MaterialTheme.colorScheme.primary)
                Spacer(Modifier.width(8.dp))
                Text(mine.course.title, fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
            }
            Text(stringResource(mine.course.kind.label()), style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
            val completion = mine.completion
            when {
                completion?.passed == true -> Text(
                    stringResource(Res.string.course_passed_with, completion.score),
                    color = TrainingDoneColor, fontWeight = FontWeight.Bold
                )
                mine.overdueOn(today) -> Text(
                    stringResource(Res.string.course_overdue_since, formatDay(mine.assigned.dueOn!!)),
                    color = TrainingOverdueColor, fontWeight = FontWeight.Bold
                )
                mine.assigned.dueOn != null -> Text(stringResource(Res.string.course_due_on, formatDay(mine.assigned.dueOn!!)))
            }
            if (!mine.passed && mine.course.pages.isNotEmpty()) {
                val seen = mine.progress?.pagesSeen?.count { id -> mine.course.pages.any { it.id == id } } ?: 0
                Text(stringResource(Res.string.course_pages_seen, seen, mine.course.pages.size), style = MaterialTheme.typography.bodySmall)
            }
            CardActions { Button(onClick = onOpen) { Text(stringResource(Res.string.course_open)) } }
        }
    }
}

@Composable
private fun ManagedCourseCard(course: Course, onEdit: (() -> Unit)?, onProgress: () -> Unit, onPreview: () -> Unit) {
    SurfaceCard(
        modifier = Modifier.fillMaxWidth().testTag(managedCourseTag(course.id))
    ) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text(course.title.ifBlank { "…" }, fontWeight = FontWeight.Bold)
            Text(
                stringResource(course.kind.label()) + " · " +
                    if (course.isPublished) stringResource(Res.string.course_status_published, course.version) else stringResource(Res.string.course_status_draft),
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            // SH-230: the card's actions all in one style (they were text buttons and one outlined).
            CardActions {
                if (onEdit != null) CardAction(Res.string.course_edit, onEdit)
                CardAction(Res.string.course_preview, onPreview)
                if (course.isPublished) CardAction(Res.string.course_progress, onProgress)
            }
        }
    }
}

@Composable
private fun TeamRow(row: TeamTrainingRow, onOpenOhs: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth().testTag(teamTrainingTag(row.userId)).padding(horizontal = 16.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(Modifier.weight(1f)) {
            Text(row.name, fontWeight = FontWeight.Bold)
            val status = row.status
            Text(
                if (status == null) stringResource(Res.string.training_status_none)
                else stringResource(status.state.label()) + " · " + stringResource(Res.string.training_status_counts, status.done, status.done + status.pending),
                style = MaterialTheme.typography.bodySmall,
                color = when (status?.state) {
                    TrainingState.DONE -> TrainingDoneColor
                    TrainingState.OVERDUE -> TrainingOverdueColor
                    else -> MaterialTheme.colorScheme.onSurfaceVariant
                }
            )
        }
        Spacer(Modifier.width(8.dp))
        // SH-230: the same button as on one's own OHS card (it was a text button here).
        CardAction(Res.string.ohs_open_record, onOpenOhs)
    }
}

/** SH-230: a card's actions, in one row at its end (wrapping on a narrow phone). */
@Composable
private fun ColumnScope.CardActions(content: @Composable () -> Unit) {
    FlowRow(
        modifier = Modifier.align(Alignment.End).padding(top = 4.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.End),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) { content() }
}

/**
 * SH-230: every action on the training cards and the team's rows: the same outlined button
 * (before, some were text buttons and some outlined). Compact, so a card's three fit a phone.
 */
@Composable
private fun CardAction(label: StringResource, onClick: () -> Unit) {
    OutlinedButton(onClick = onClick, contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp)) {
        Text(stringResource(label))
    }
}

/**
 * SH-126: on the dashboards, when the person has courses to do or an OHS instruction to confirm.
 * A tap opens Training.
 */
@Composable
fun TrainingCard(viewModel: MyTrainingViewModel, onOpen: (workplaceId: String) -> Unit, modifier: Modifier = Modifier) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val workplaceId = state.workplaceId
    // SH-247: read after the dashboard: the card unfolds instead of popping in (and folds away).
    val shownWorkplaceId = com.evoapps.evoshift.ui.theme.rememberLastNonNull(workplaceId.takeIf { state.shows })
    com.evoapps.evoshift.ui.theme.MotionVisibility(state.shows && workplaceId != null && shownWorkplaceId != null) {
    val workplaceId = shownWorkplaceId ?: return@MotionVisibility
    RegularCard(modifier.fillMaxWidth().testTag(TRAINING_CARD), onClick = { onOpen(workplaceId) }) {
        Row(Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
            Icon(Icons.Default.School, contentDescription = null, tint = DarkGold, modifier = Modifier.size(20.dp))
            Spacer(Modifier.width(8.dp))
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(
                    stringResource(Res.string.training_title),
                    style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                    color = DeepNavy.copy(alpha = 0.7f)
                )
                if (state.toDo > 0) Text(stringResource(Res.string.training_card_to_do, state.toDo), color = DeepNavy)
                if (state.overdue > 0) Text(stringResource(Res.string.training_card_overdue, state.overdue), color = TrainingOverdueColor, fontWeight = FontWeight.Bold)
                if (state.confirmations > 0) Text(stringResource(Res.string.training_card_confirmations, state.confirmations), color = DeepNavy, fontWeight = FontWeight.Bold)
            }
            Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, contentDescription = null, tint = DarkGold)
        }
    }
    }
}
